import { useRequiredContext } from "@suse-o11y/use-required-context";
import React from "react";

import { PRODUCT_NAME } from "~/env";

interface BrowserPageTitleContextValue {
	fragments: Array<string>;
	replaceFragments: React.Dispatch<Array<string>>;
}

const BrowserPageTitleContext = React.createContext<null | BrowserPageTitleContextValue>(null);

export const BrowserPageTitleProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [fragments, setFragments] = React.useState<Array<string>>([]);
	const title = fragments
		.filter((fragment) => fragment) // filter out empty strings
		.reverse() // reverse array to show sub views/pages first
		.join(" - ");

	React.useEffect(() => {
		document.title = title ? `${title} | ${PRODUCT_NAME}` : PRODUCT_NAME;
	}, [title]);

	const replaceFragments = React.useCallback((newFragments: Array<string>) => {
		// Here newFragments replaces the current fragments only in case when newFragments is longer
		// that prevents the parent's fragments from replacing the child's because React renders
		// components bottom-to-top (children-to-parents) so in this example:

		// const Box: React.FC<{
		//   name: string
		// }> = ({ name, children }) => {
		//   React.useEffect(() => {
		//     console.log(`${name} renders`)
		//   }, [name])
		//   return <>{children}</>
		// }

		// ReactDOM.render(
		//   <Box name="parent">
		//     <Box name="child" />
		//   </Box>
		// )

		// the console output will be
		// - "child renders"
		// - "parent renders"

		// By replacing the fragments only with longer fragments guarantees that the parent's fragments
		// never overrides a child's fragments.
		setFragments((current) => (newFragments.length > current.length ? newFragments : current));

		return () => {
			// The cleanup function drops the last piece of fragments when the component unmounts.
			// This way the resulting fragments can be either replaced with new fragments:
			// - ["overview", "view"] -> ["settings"] by calling `replaceFragments` from the settings component in
			// that case right after `current.slice` down below
			// or shortens the child's fragments:
			// - ["overview", "view"] -> ["overview"] by calling `current.slice` down below
			setFragments((current) => current.slice(0, newFragments.length - 1));
		};
	}, []);

	const contextProviderProps = React.useMemo(
		() => ({
			fragments: [],
			replaceFragments,
		}),
		[replaceFragments],
	);

	return <BrowserPageTitleContext.Provider value={contextProviderProps}>{children}</BrowserPageTitleContext.Provider>;
};

export const BrowserPageTitle: React.FC<
	React.PropsWithChildren<{
		fragment: string;
	}>
> = ({ fragment, children }) => {
	const context = useRequiredContext(BrowserPageTitleContext, {
		providerName: "BrowserPageTitleProvider",
	});

	const { fragments, replaceFragments } = context;

	React.useEffect(() => replaceFragments([...fragments, fragment]), [fragment, fragments, replaceFragments]);

	const contextProviderProps = React.useMemo(
		() => ({
			fragments: [...fragments, fragment],
			replaceFragments,
		}),
		[fragments, fragment, replaceFragments],
	);

	return <BrowserPageTitleContext.Provider value={contextProviderProps}>{children}</BrowserPageTitleContext.Provider>;
};
